import { useState, useEffect } from "react";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import { Upload, X, Loader2 } from "lucide-react";

/* Compact upload control — single image, returns the upload id for storage
   alongside the park or plot record. Falls back to a placeholder when blank. */
export function PhotoUploader({ value, onChange, label = "Photo", testid }) {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post("/uploads/anon", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        baseURL: adminApi.defaults.baseURL.replace("/api/admin", "/api"),
      });
      onChange(data.id);
      toast.success("Photo uploaded");
    } catch (err) {
      // Fallback: use the public uploads endpoint instead — works without admin auth.
      try {
        const fd2 = new FormData();
        fd2.append("file", file);
        const r2 = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads`, { method: "POST", body: fd2 });
        const d2 = await r2.json();
        if (d2.id) { onChange(d2.id); toast.success("Photo uploaded"); }
        else throw err;
      } catch {
        toast.error("Photo upload failed");
      }
    } finally { setUploading(false); }
  };

  const url = value ? `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${value}` : null;

  return (
    <div data-testid={testid}>
      <p className="text-[10px] tracking-[0.22em] uppercase text-[#475569] font-medium mb-1.5">{label}</p>
      <div className="flex items-center gap-3">
        <div className="w-20 h-20 rounded-lg overflow-hidden bg-[#F7F4ED] border border-[#E2DCD0] flex items-center justify-center shrink-0">
          {url
            ? <img src={url} alt="" className="w-full h-full object-cover"/>
            : <Upload size={20} className="text-[#94A3B8]"/>}
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs text-[#0F766E] hover:underline cursor-pointer inline-flex items-center gap-1.5">
            {uploading ? <Loader2 size={12} className="animate-spin"/> : <Upload size={12}/>}
            {value ? "Replace photo" : "Upload photo"}
            <input type="file" accept="image/*" onChange={handleFile} className="hidden"/>
          </label>
          {value && (
            <button onClick={() => onChange("")} className="text-[11px] text-[#B91C1C] hover:underline inline-flex items-center gap-1">
              <X size={11}/> Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* Lightweight modal — reused for Add and Edit on parks and plots. */
export function FormDialog({ open, onClose, title, onSubmit, submitLabel = "Save", children, testid, busy }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose}/>
      <div className="relative bg-white rounded-xl border border-[#E2DCD0] shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <header className="px-5 py-4 border-b border-[#E2DCD0] bg-[#F7F4ED]/60 flex items-center justify-between">
          <h3 className="font-display text-xl text-[#071A33]">{title}</h3>
          <button onClick={onClose} className="text-[#475569] hover:text-[#071A33]"><X size={18}/></button>
        </header>
        <form
          onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
          className="p-5 space-y-4 overflow-y-auto"
        >
          {children}
          <footer className="flex items-center justify-end gap-2 pt-3 border-t border-[#E2DCD0]">
            <button type="button" onClick={onClose} className="text-sm px-4 py-2 rounded-md text-[#475569] hover:bg-[#F7F4ED]">Cancel</button>
            <button type="submit" disabled={busy} className="text-sm px-4 py-2 rounded-md bg-[#064E2E] text-white hover:bg-[#0a5b55] disabled:opacity-50 inline-flex items-center gap-2">
              {busy && <Loader2 size={13} className="animate-spin"/>}
              {submitLabel}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}

export function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "col-span-2" : ""}`}>
      <span className="text-[10px] tracking-[0.22em] uppercase text-[#475569] font-medium block mb-1">{label}</span>
      {children}
    </label>
  );
}
